/**
 * The notifications inbox and preferences as fixture data (W9 §C.5, §A.5).
 *
 * 9 rows across 6 event types, scoped to the viewer (`user-1`) except one that belongs to
 * `user-2` — present only to prove the ownership gate `markRead`/`archive` enforce, the same
 * one the real `PUT /api/notifications/{id}` handler enforces and the dotted
 * `notifications.update` route notoriously does NOT (§E-12, backend follow-up B14). 4 rows are
 * unread, 2 are archived, and exactly ONE row is both — which is what makes `unseen` (3) a
 * different number from the raw unread count (4): `unseen` counts only rows that are neither
 * archived nor read, the same rule `NotificationsController.List` applies.
 *
 * Preferences seed one legacy scalar (`documents.publish: true` — app-only, never email) and
 * one explicit per-channel object (`runs.failed: {app:true, email:false}`), so
 * `resolveChannel()` is exercised against both shapes from a single fixture.
 */
import { ApiError, NotFoundError } from "~/api/errors";
import { isKnownNotificationEvent } from "~/api/notificationEvents";
import type { Notification, NotificationChannel, NotificationPreferences } from "~/api/types";

const BASE_TIME = Date.parse("2026-08-12T09:00:00.000Z");
const hoursAgo = (h: number) => new Date(BASE_TIME - h * 3600_000).toISOString();

const VIEWER_ID = "user-1";
const OTHER_USER_ID = "user-2";

const ADA = { id: "user-1", name: "Ada Lovelace", avatarUrl: null as string | null };
const GRACE = { id: "user-2", name: "Grace Hopper", avatarUrl: null as string | null };

interface Row extends Notification {
  ownerId: string;
}

const SEED: Row[] = [
  {
    id: "notif-1",
    ownerId: VIEWER_ID,
    userId: VIEWER_ID,
    viewedAt: null,
    archivedAt: null,
    createdAt: hoursAgo(1),
    event: "comments.mentioned",
    actorId: GRACE.id,
    actor: GRACE,
    commentId: "comment-1",
    comment: { id: "comment-1", createdBy: GRACE },
    documentId: "doc-1",
    document: { id: "doc-1", title: "Access review runbook" },
    revisionId: null,
    collectionId: null,
  },
  {
    id: "notif-2",
    ownerId: VIEWER_ID,
    userId: VIEWER_ID,
    viewedAt: null,
    archivedAt: null,
    createdAt: hoursAgo(2),
    event: "comments.mentioned",
    actorId: GRACE.id,
    actor: GRACE,
    commentId: "comment-2",
    comment: { id: "comment-2", createdBy: GRACE },
    documentId: "doc-2",
    document: { id: "doc-2", title: "Onboarding checklist" },
    revisionId: null,
    collectionId: null,
  },
  {
    id: "notif-3",
    ownerId: VIEWER_ID,
    userId: VIEWER_ID,
    viewedAt: null,
    archivedAt: null,
    createdAt: hoursAgo(3),
    event: "comments.create",
    actorId: GRACE.id,
    actor: GRACE,
    commentId: "comment-3",
    comment: { id: "comment-3", createdBy: GRACE },
    documentId: "doc-1",
    document: { id: "doc-1", title: "Access review runbook" },
    revisionId: null,
    collectionId: null,
  },
  {
    id: "notif-4",
    ownerId: VIEWER_ID,
    userId: VIEWER_ID,
    viewedAt: null,
    archivedAt: hoursAgo(0.5),
    createdAt: hoursAgo(4),
    event: "runs.failed",
    actorId: null,
    actor: null,
    commentId: null,
    comment: null,
    documentId: null,
    document: null,
    revisionId: null,
    collectionId: null,
  },
  {
    id: "notif-5",
    ownerId: VIEWER_ID,
    userId: VIEWER_ID,
    viewedAt: hoursAgo(1),
    archivedAt: hoursAgo(1),
    createdAt: hoursAgo(20),
    event: "documents.update",
    actorId: ADA.id,
    actor: ADA,
    commentId: null,
    comment: null,
    documentId: "doc-1",
    document: { id: "doc-1", title: "Access review runbook" },
    revisionId: "rev-div-2",
    collectionId: null,
  },
  {
    id: "notif-6",
    ownerId: VIEWER_ID,
    userId: VIEWER_ID,
    viewedAt: hoursAgo(2),
    archivedAt: null,
    createdAt: hoursAgo(21),
    event: "documents.update",
    actorId: ADA.id,
    actor: ADA,
    commentId: null,
    comment: null,
    documentId: "doc-2",
    document: { id: "doc-2", title: "Onboarding checklist" },
    revisionId: null,
    collectionId: null,
  },
  {
    id: "notif-7",
    ownerId: VIEWER_ID,
    userId: VIEWER_ID,
    viewedAt: hoursAgo(30),
    archivedAt: null,
    createdAt: hoursAgo(48),
    event: "flows.published",
    actorId: null,
    actor: null,
    commentId: null,
    comment: null,
    documentId: null,
    document: null,
    revisionId: null,
    collectionId: null,
  },
  {
    id: "notif-8",
    ownerId: VIEWER_ID,
    userId: VIEWER_ID,
    viewedAt: hoursAgo(40),
    archivedAt: null,
    createdAt: hoursAgo(50),
    event: "tasks.fired",
    actorId: null,
    actor: null,
    commentId: null,
    comment: null,
    documentId: null,
    document: null,
    revisionId: null,
    collectionId: null,
  },
  // Belongs to someone else — never returned by `list()`, present only to prove the ownership
  // gate on markRead/archive (§E-12).
  {
    id: "notif-9",
    ownerId: OTHER_USER_ID,
    userId: OTHER_USER_ID,
    viewedAt: null,
    archivedAt: null,
    createdAt: hoursAgo(1),
    event: "comments.create",
    actorId: ADA.id,
    actor: ADA,
    commentId: "comment-9",
    comment: { id: "comment-9", createdBy: ADA },
    documentId: "doc-2",
    document: { id: "doc-2", title: "Onboarding checklist" },
    revisionId: null,
    collectionId: null,
  },
];

export interface FakeNotificationsDeps {
  requireSession(): unknown;
  delay<T>(value: T): Promise<T>;
  viewerId(): string;
}

export function createFakeNotifications(deps: FakeNotificationsDeps) {
  const rows: Row[] = SEED.map((row) => ({ ...row }));
  let preferences: Record<string, unknown> = {
    // Legacy scalar — app only, NEVER email, no matter how it reads.
    "documents.publish": true,
    // Explicit per-channel object.
    "runs.failed": { app: true, email: false },
  };

  const own = () => rows.filter((row) => row.ownerId === deps.viewerId());

  const findOwn = (id: string): Row => {
    const found = rows.find((row) => row.id === id && row.ownerId === deps.viewerId());
    if (!found) {
      // The exact posture of the real ownership-checked handler: a row that exists but is not
      // yours reads as "not found," never "forbidden" (§E-12).
      throw new NotFoundError("Notification not found");
    }
    return found;
  };

  return {
    list: async (
      query: { archived?: boolean; offset?: number; limit?: number } = {},
    ): Promise<{
      pagination: { offset: number; limit: number; total: number };
      notifications: Notification[];
      unseen: number;
    }> => {
      deps.requireSession();
      const mine = own();
      const filtered = mine.filter((row) =>
        query.archived === undefined
          ? true
          : query.archived
            ? row.archivedAt !== null
            : row.archivedAt === null,
      );
      const sorted = [...filtered].sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
      const offset = query.offset ?? 0;
      const limit = query.limit ?? 25;
      // ⚠ N6: `unseen` counts rows that are BOTH unarchived AND unread — computed from the
      // WHOLE owned set, never from the page just filtered/sliced above, matching the server's
      // "unseen is unaffected by eventType/archived filters other than being scoped to you."
      const unseen = mine.filter((row) => row.viewedAt === null && row.archivedAt === null).length;
      return deps.delay({
        pagination: { offset, limit, total: filtered.length },
        notifications: sorted
          .slice(offset, offset + limit)
          .map(({ ownerId: _ownerId, ...rest }) => rest),
        unseen,
      });
    },

    markRead: async (id: string): Promise<void> => {
      deps.requireSession();
      const found = findOwn(id);
      found.viewedAt = new Date(BASE_TIME).toISOString();
      return deps.delay(undefined);
    },

    archive: async (id: string): Promise<void> => {
      deps.requireSession();
      const found = findOwn(id);
      found.archivedAt = new Date(BASE_TIME).toISOString();
      return deps.delay(undefined);
    },

    markAllRead: async (): Promise<number> => {
      deps.requireSession();
      const mine = own().filter((row) => row.viewedAt === null);
      for (const row of mine) {
        row.viewedAt = new Date(BASE_TIME).toISOString();
      }
      return deps.delay(mine.length);
    },

    preferences: async (): Promise<NotificationPreferences> => {
      deps.requireSession();
      return deps.delay({ ...preferences } as NotificationPreferences);
    },

    setChannel: async (input: {
      eventType: string;
      channel: NotificationChannel;
      enabled: boolean;
    }): Promise<void> => {
      deps.requireSession();
      // The one line that would have failed the pre-W9 section's four invented event ids —
      // `document.mention`, `document.shared`, `collection.created`, `digest.weekly` — none of
      // which exist in this taxonomy.
      if (!isKnownNotificationEvent(input.eventType)) {
        throw new ApiError(`Unknown notification event type '${input.eventType}'.`, 400);
      }
      const current = preferences[input.eventType];
      const next =
        typeof current === "object" && current !== null
          ? { ...(current as Record<string, boolean>) }
          : {};
      next[input.channel] = input.enabled;
      preferences = { ...preferences, [input.eventType]: next };
      return deps.delay(undefined);
    },
  };
}
